import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { clearPremiumCache } from "@/lib/premiumApi";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";

export default function PremiumSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/premium/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearPremiumCache();
          setStatus("paid");
          return;
        }
      } catch (_) {
        if (attempts >= 10) { setStatus("error"); return; }
      }
      if (attempts < 10) setTimeout(poll, 1800); else setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="premium-success-page" className="bg-white text-[#0a0a0a] min-h-screen flex flex-col">
      <Seo title="Premium confirmation — Universtrend" description="Confirming your Premium subscription." />
      <Header />
      <main className="flex-1 flex items-center">
        <div className="mx-auto max-w-[900px] w-full px-6 lg:px-10 py-16 text-center">
          {status === "polling" && (
            <>
              <div className="overline text-[#52525b] mb-3">Confirming with Stripe…</div>
              <h1 className="font-display font-extrabold text-5xl lg:text-7xl tracking-tighter">Hang tight.</h1>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="overline text-[#ff3b30] mb-3">Premium active ✓</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                Welcome to<br /><span className="text-[#ff3b30]">Premium.</span>
              </h1>
              <p className="mt-6 text-[#52525b] max-w-xl mx-auto">
                Ads are gone. The AI Business Builder is unlocked on every trend page.
                Go find your next business.
              </p>
              <Link
                to="/"
                data-testid="premium-success-home"
                className="hard-shadow inline-block mt-10 border border-black bg-black text-white px-6 py-3 overline"
              >
                Explore the Atlas →
              </Link>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <div className="overline text-[#52525b] mb-3">Still processing</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter">Almost there.</h1>
              <p className="mt-6 text-[#52525b] max-w-xl mx-auto">
                Your payment may still be confirming. Refresh this page in a minute —
                or check your account on the <Link to="/premium" className="underline">Premium page</Link>.
              </p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
